import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CardapioProdutoDTO } from '../../../../models/cardapio-digital/cardapio-digital.model';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';
import {
  exigeConfiguracao as exigeConfiguracaoUtil,
  semEstoqueDisponivel as semEstoqueDisponivelUtil,
} from '../../utils/cardapio-produto.util';

@Component({
  selector: 'app-cardapio-produto-card',
  templateUrl: './cardapio-produto-card.component.html',
  styleUrls: ['./cardapio-produto-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardapioProdutoCardComponent {
  private _produto!: CardapioProdutoDTO;
  /** True quando a imagem falhou ao carregar (mostra o placeholder, nao o alt). */
  imagemComErro = false;

  @Input() set produto(value: CardapioProdutoDTO) {
    this._produto = value;
    this.imagemComErro = false; // reseta ao trocar de produto
  }
  get produto(): CardapioProdutoDTO {
    return this._produto;
  }

  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';

  private _quantidadeNoCarrinho = 0;
  /** True enquanto o badge roda a animacao de "pop" (limpo no animationend). */
  badgePulsando = false;

  /**
   * Quantidade deste produto no carrinho. Ao aumentar, aciona o pulso do badge —
   * retorno visual da adicao, ja que o cardapio digital nao renderiza toast.
   */
  @Input() set quantidadeNoCarrinho(value: number) {
    const anterior = this._quantidadeNoCarrinho;
    this._quantidadeNoCarrinho = value ?? 0;
    if (this._quantidadeNoCarrinho > anterior) {
      this.badgePulsando = true;
    }
  }
  get quantidadeNoCarrinho(): number {
    return this._quantidadeNoCarrinho;
  }

  @Output() adicionar = new EventEmitter<CardapioProdutoDTO>();
  @Output() adicionarRapido = new EventEmitter<CardapioProdutoDTO>();
  @Output() compartilhar = new EventEmitter<CardapioProdutoDTO>();

  get temImagem(): boolean {
    return !!this.produto.foto;
  }

  get urlImagem(): string {
    return this.produto.foto || '';
  }

  get precoFormatado(): string {
    return this.formatarPreco(this.produto.preco);
  }

  get precoPromocionalFormatado(): string | null {
    if (!this.produto.precoPromocional) return null;
    return this.formatarPreco(this.produto.precoPromocional);
  }

  /** Item marcado como novidade (vigente — vencimento ja resolvido no backend) */
  get ehNovidade(): boolean {
    return !!this.produto.novidade;
  }

  /** Item marcado como promocao (vigente — vencimento ja resolvido no backend) */
  get emPromocao(): boolean {
    return !!this.produto.promocao;
  }

  /** Percentual de desconto para badge (ex: -20%) */
  get percentualDesconto(): number | null {
    if (!this.produto.precoPromocional || this.produto.precoPromocional >= this.produto.preco) return null;
    return Math.round((1 - this.produto.precoPromocional / this.produto.preco) * 100);
  }

  /** ID com 4 digitos (0001, 0042...) */
  id4(n: number): string {
    return String(n ?? 0).padStart(4, '0');
  }

  /** Sem saldo para adicionar mais unidades (so quando ha controle de estoque). */
  get semEstoqueDisponivel(): boolean {
    return semEstoqueDisponivelUtil(this.produto, this.quantidadeNoCarrinho);
  }

  /** Produto que exige escolha antes de ir ao carrinho (variante ou grupo obrigatorio). */
  get exigeConfiguracao(): boolean {
    return exigeConfiguracaoUtil(this.produto);
  }

  /** Exibe o botao do card (adicionar OU configurar); some so sem saldo em estoque. */
  get mostraBotaoAcao(): boolean {
    return !!this.produto && !this.semEstoqueDisponivel;
  }

  /**
   * Clique (ou Enter/Espaco) no card inteiro abre a tela de detalhe/adicao do
   * produto, reutilizando o mesmo Output que o antigo botao "Adicionar" emitia.
   */
  onAbrirDetalhe(): void {
    this.adicionar.emit(this.produto);
  }

  /**
   * Acao do botao do card. Impede a propagacao (senao o card inteiro tambem
   * dispararia): produto sem escolha obrigatoria vai direto ao carrinho, produto
   * que exige configuracao abre o detalhe.
   */
  onAcaoRapida(event: Event): void {
    event.stopPropagation();
    if (this.semEstoqueDisponivel) return;

    if (this.exigeConfiguracao) {
      this.adicionar.emit(this.produto);
      return;
    }

    this.adicionarRapido.emit(this.produto);
  }

  /**
   * Compartilha este produto especifico. Impede que o clique propague para o
   * card (que abriria o detalhe) antes de emitir o produto para o componente pai.
   */
  onCompartilhar(event: Event): void {
    event.stopPropagation();
    this.compartilhar.emit(this.produto);
  }

  private formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }
}
